import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Tag, Progress, Drawer, Spin, Empty,
} from 'antd';
import {
  Users, IndianRupee, AlertTriangle, AlertCircle, Info,
  ArrowUpRight, ArrowDownRight, Activity, Zap, Ticket, Bug,
  ChevronRight, Wallet, Flame, ShieldAlert, UserPlus,
} from 'lucide-react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  Tooltip as ChartTooltip, Legend as ChartLegend,
} from 'chart.js';
import useApiMethods from '../../lib/useApiMethods';
import {
  DASHBOARD_OVERVIEW, HEALTH_SCORE, ACTIVE_ALERTS, CROSS_SIGNALS,
  TARGETS_VS_ACTUAL, RECEIVABLES_SUMMARY, TRENDS,
} from '../../constants/api';
import { MetricCard, HeadlineCard, DataDrawer, Surface } from '../../components/insights';
import { AuthContext } from '../../context/AuthContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip, ChartLegend);

const formatLakhs = (amount) => `₹${(amount / 100000).toFixed(1)}L`;

const Dashboard = () => {
  const [overview, setOverview] = useState(null);
  const [healthScore, setHealthScore] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [signals, setSignals] = useState([]);
  const [targetsVsActual, setTargetsVsActual] = useState([]);
  const [receivables, setReceivables] = useState({});
  const [trends, setTrends] = useState({ time_series: [], deltas: {} });
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [kpiDrawer, setKpiDrawer] = useState(null); // {key, title, subtitle, series}

  const { fetch } = useApiMethods();
  const { hasPermission } = useContext(AuthContext);
  const canSeeFinance = hasPermission('finance_sensitive', 'view');

  const fetchData = useCallback(async () => {
    try {
      const today = new Date();
      const periodStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

      const [overviewRes, healthRes, alertsRes, signalsRes, targetsRes, recRes, trendsRes] = await Promise.all([
        fetch(DASHBOARD_OVERVIEW),
        fetch(HEALTH_SCORE),
        fetch(ACTIVE_ALERTS),
        fetch(CROSS_SIGNALS),
        canSeeFinance ? fetch(TARGETS_VS_ACTUAL, { period_start: periodStart, period: 'monthly' }) : Promise.resolve(null),
        canSeeFinance ? fetch(RECEIVABLES_SUMMARY) : Promise.resolve(null),
        hasPermission('trends', 'view') ? fetch(TRENDS, { period: 'week' }) : Promise.resolve(null),
      ]);

      if (overviewRes.status === 'success') setOverview(overviewRes.data);
      if (healthRes.status === 'success') setHealthScore(healthRes.data);
      if (alertsRes.status === 'success') setAlerts(alertsRes.data || []);
      if (signalsRes.status === 'success') setSignals(signalsRes.data || []);
      if (targetsRes?.status === 'success') setTargetsVsActual(targetsRes.data || []);
      if (recRes?.status === 'success') setReceivables(recRes.data || {});
      if (trendsRes?.status === 'success') setTrends(trendsRes.data || { time_series: [], deltas: {} });
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><Spin size="large" /></div>;
  }

  if (!overview) {
    return <Empty description="Unable to load dashboard data" className="mt-20" />;
  }

  const { hr = {}, sales = {}, finance = {}, support = {} } = overview;
  const score = healthScore?.score || 0;
  const breakdown = healthScore?.breakdown || {};
  const departmentHealthData = overview.department_health || [];
  const revenueTarget = targetsVsActual.find((t) => t.metric === 'revenue');

  const criticalSignals = signals.filter(s => s.severity === 'red');
  const warningSignals = signals.filter(s => s.severity === 'amber');
  const criticalAlerts = alerts.filter(a => a.severity === 'critical');

  // Determine urgency
  const urgentCount = criticalSignals.length + criticalAlerts.length;

  // ── Helpers ──
  const ScoreRing = ({ value, size = 120, label }) => {
    const color = value >= 75 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444';
    return (
      <div className="flex flex-col items-center">
        <Progress
          type="circle"
          percent={value}
          size={size}
          strokeColor={color}
          strokeWidth={8}
          format={() => (
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color }}>{value}</div>
            </div>
          )}
        />
        {label && <span className="text-xs text-muted-foreground mt-2">{label}</span>}
      </div>
    );
  };

  // Legacy icon-bg class → intent mapping (keeps existing call sites working)
  const iconBgToIntent = (bg = '') => {
    if (bg.includes('emerald') || bg.includes('green')) return 'positive';
    if (bg.includes('rose') || bg.includes('red')) return 'negative';
    if (bg.includes('amber') || bg.includes('yellow') || bg.includes('orange')) return 'warning';
    if (bg.includes('sky') || bg.includes('cyan') || bg.includes('blue')) return 'info';
    if (bg.includes('indigo') || bg.includes('violet') || bg.includes('purple')) return 'primary';
    return 'neutral';
  };

  // Bridge: old KpiCard(icon, iconBg, title, value, subtitle, trend, alert, metricKey)
  // → new MetricCard with sparkline pulled from trends.time_series[metricKey]
  const KpiCard = ({ icon, iconBg, title, value, subtitle, trend, alert, metricKey, goodUp = true }) => {
    const IconComp = icon?.type; // lucide icons passed as JSX element
    const series = metricKey && trends.time_series?.length
      ? trends.time_series.map((t) => t[metricKey] || 0)
      : null;
    const delta = metricKey && trends.deltas?.[metricKey]
      ? trends.deltas[metricKey].delta_pct
      : (trend !== undefined && trend !== null ? trend : null);
    const intent = alert ? 'negative' : iconBgToIntent(iconBg);
    return (
      <MetricCard
        icon={IconComp}
        title={title}
        value={value}
        subtitle={subtitle}
        intent={intent}
        delta={delta}
        goodUp={goodUp}
        sparkline={series}
        onClick={metricKey ? () => setKpiDrawer({ key: metricKey, title, subtitle, value }) : undefined}
      />
    );
  };

  const InsightRow = ({ icon, color, message, action }) => (
    <div className={`flex items-start gap-3 p-3 rounded-xl border-l-4 ${
      color === 'red' ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-500' :
      color === 'amber' ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-500' :
      'bg-sky-50 dark:bg-sky-950/30 border-sky-500'
    }`}>
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">{message}</p>
        {action && <p className="text-xs text-muted-foreground mt-1">{action}</p>}
      </div>
    </div>
  );

  // ── Compute decision insights ──
  const insights = [];

  if (urgentCount > 0) {
    insights.push({
      icon: <Flame className="w-5 h-5 text-red-500" />,
      color: 'red',
      message: `${urgentCount} critical issue${urgentCount > 1 ? 's' : ''} need your attention right now`,
      action: criticalSignals[0]?.message,
    });
  }

  const budgetUsed = finance.budget_utilization || 0;
  if (budgetUsed > 85) {
    insights.push({
      icon: <Wallet className="w-5 h-5 text-red-500" />,
      color: 'red',
      message: `Budget at ${budgetUsed}% — approaching limit`,
    });
  }

  if ((support.production_bugs || 0) > 5) {
    insights.push({
      icon: <Bug className="w-5 h-5 text-red-500" />,
      color: 'red',
      message: `${support.production_bugs} production bugs open — customer impact risk`,
    });
  }

  // ── Dept health chart ──
  const deptChartData = {
    labels: departmentHealthData.map(d => d.department),
    datasets: [{
      data: departmentHealthData.map(d => d.vs_target || 0),
      backgroundColor: departmentHealthData.map(d =>
        d.status === 'on_track' ? '#10b981' : d.status === 'at_risk' ? '#f59e0b' : '#ef4444'
      ),
      borderRadius: 8,
      barThickness: 32,
    }],
  };
  const deptChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: 'rgba(148,163,184,0.15)' }, ticks: { color: 'rgba(148,163,184,0.9)', callback: v => `${v}%` } },
    },
  };

  // ── Alerts donut ──
  const sevCounts = alerts.reduce((acc, a) => {
    const s = (a.severity || 'info').toLowerCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, { critical: 0, warning: 0, info: 0 });

  const donutData = {
    labels: ['Critical', 'Warning', 'Info'],
    datasets: [{
      data: [sevCounts.critical, sevCounts.warning, sevCounts.info],
      backgroundColor: ['#ef4444', '#f59e0b', '#10b981'],
      borderWidth: 0,
    }],
  };
  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
  };

  // ━━ Founder's Digest state logic ━━
  const digestState = urgentCount > 0 ? 'critical' : (warningSignals.length > 0 ? 'warning' : (score >= 75 ? 'healthy' : 'info'));
  const digestTitle = urgentCount > 0
    ? `${urgentCount} critical ${urgentCount === 1 ? 'item needs' : 'items need'} your attention`
    : warningSignals.length > 0
      ? `${warningSignals.length} ${warningSignals.length === 1 ? 'area' : 'areas'} trending off-track`
      : `Health score ${score}/100 — all departments ${score >= 75 ? 'on track' : 'stable'}`;
  const digestDesc = urgentCount > 0
    ? `${criticalSignals.length > 0 ? `${criticalSignals.length} red-level signal${criticalSignals.length > 1 ? 's' : ''}` : ''}${criticalSignals.length > 0 && criticalAlerts.length > 0 ? ' · ' : ''}${criticalAlerts.length > 0 ? `${criticalAlerts.length} critical alert${criticalAlerts.length > 1 ? 's' : ''}` : ''}`
    : `Headcount ${hr.headcount || 0}${canSeeFinance ? ` · Bookings ${formatLakhs(sales.bookings_this_month || sales.revenue_this_month || 0)} · Cash ${formatLakhs(sales.cash_collected_this_month || 0)} · Pipeline ${formatLakhs(sales.pipeline_total || 0)}` : ''}`;

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen text-foreground">

      {/* ━━ Founder's Digest ━━ */}
      <HeadlineCard
        state={digestState}
        title={digestTitle}
        description={digestDesc}
        metrics={[
          { label: 'Health', value: `${score}/100` },
          ...(canSeeFinance ? [
            { label: 'Bookings', value: formatLakhs(sales.bookings_this_month || sales.revenue_this_month || 0), hint: 'Contract value' },
            { label: 'Cash Collected', value: formatLakhs(sales.cash_collected_this_month || 0), hint: 'Received this month' },
            { label: 'Pipeline', value: formatLakhs(sales.pipeline_total || 0) },
          ] : []),
        ]}
        actions={urgentCount > 0 ? [
          { label: `Review ${urgentCount} items`, intent: 'primary', onClick: () => { const el = document.getElementById('needs-attention'); if (el) el.scrollIntoView({ behavior: 'smooth' }); } },
        ] : []}
      />

      {/* ━━ Section 1: Decision Bar ━━ */}
      {insights.length > 0 && (
        <div id="needs-attention" className="rounded-2xl bg-card text-card-foreground border border-border shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="w-5 h-5 text-indigo-500" />
            <h3 className="font-semibold text-foreground">Needs Your Attention</h3>
            <Tag color={urgentCount > 0 ? 'red' : 'orange'} className="ml-auto">{insights.length} items</Tag>
          </div>
          <div className="space-y-3">
            {insights.map((ins, idx) => (
              <InsightRow key={idx} {...ins} />
            ))}
          </div>
        </div>
      )}

      {/* ━━ Section 2: Health Score + KPI Cards ━━ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Health Score — large visual */}
        <div className="lg:col-span-3">
          <div className="rounded-2xl bg-card text-card-foreground border border-border shadow-sm p-6 h-full flex flex-col items-center justify-center">
            <ScoreRing value={score} size={140} />
            <h4 className="font-semibold text-foreground mt-4">Org Health</h4>
            <p className="text-xs text-muted-foreground mt-1">
              {score >= 75 ? 'Looking good' : score >= 50 ? 'Needs attention' : 'Critical issues'}
            </p>
            {/* Mini breakdown */}
            <div className="w-full mt-4 space-y-2">
              {Object.entries(breakdown).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground w-20 truncate">{key.replace(/_/g, ' ')}</span>
                  <div className="flex-1">
                    <Progress
                      percent={Math.round(val)}
                      size="small"
                      showInfo={false}
                      strokeColor={val >= 70 ? '#10b981' : val >= 40 ? '#f59e0b' : '#ef4444'}
                    />
                  </div>
                  <span className="text-[10px] font-medium text-foreground w-6 text-right">{Math.round(val)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="lg:col-span-9">
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
            <KpiCard
              icon={<Users className="w-5 h-5 text-white" />}
              iconBg="bg-indigo-500"
              title="Headcount"
              value={hr.headcount || 0}
              subtitle={`${hr.attrition_rate || 0}% attrition`}
              alert={(hr.attrition_rate || 0) > 10}
              metricKey="headcount"
              goodUp={true}
            />
            {canSeeFinance && (
            <KpiCard
              icon={<Wallet className="w-5 h-5 text-white" />}
              iconBg="bg-amber-500"
              title="Payouts Pending"
              value={(hr.reimb_pending_count || 0) + (hr.advance_pending_count || 0)}
              subtitle={`${formatLakhs((hr.reimb_pending_amount || 0) + (hr.advance_pending_amount || 0))} awaiting approval`}
              alert={((hr.reimb_pending_count || 0) + (hr.advance_pending_count || 0)) > 10}
              metricKey="reimb_pending_count"
              goodUp={false}
            />
            )}
            <KpiCard
              icon={<UserPlus className="w-5 h-5 text-white" />}
              iconBg="bg-sky-500"
              title="New Leads This Week"
              value={sales.new_leads_this_week || 0}
              subtitle={`${sales.new_leads_today || 0} today · ${sales.new_leads_this_month || 0} this month`}
              metricKey="new_leads_this_week"
              goodUp={true}
            />
            <KpiCard
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              iconBg="bg-emerald-500"
              title="Bookings This Month"
              value={formatLakhs(sales.bookings_this_month || sales.revenue_this_month || 0)}
              subtitle={revenueTarget
                ? `${Math.round((sales.bookings_this_month || sales.revenue_this_month || 0) / Number(revenueTarget.target_value) * 100)}% of ${formatLakhs(Number(revenueTarget.target_value))} target`
                : 'Closed Won contract value'}
              alert={revenueTarget && (sales.bookings_this_month || sales.revenue_this_month || 0) < Number(revenueTarget.target_value) * 0.5}
              metricKey="bookings_this_month"
              goodUp={true}
            />
            {canSeeFinance && (
            <KpiCard
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              iconBg="bg-teal-500"
              title="Cash Collected"
              value={formatLakhs(sales.cash_collected_this_month || 0)}
              subtitle={`${sales.cash_receipt_count_this_month || 0} receipts this month`}
              metricKey="cash_collected_this_month"
              goodUp={true}
            />
            )}
            {canSeeFinance && (
            <KpiCard
              icon={<Wallet className="w-5 h-5 text-white" />}
              iconBg="bg-amber-500"
              title="Budget Used"
              value={`${finance.budget_utilization || 0}%`}
              subtitle={finance.budget_utilization > 80 ? 'Approaching limit' : 'On track'}
              alert={finance.budget_utilization > 85}
              metricKey="budget_utilization"
              goodUp={false}
            />
            )}
            <KpiCard
              icon={<Ticket className="w-5 h-5 text-white" />}
              iconBg="bg-violet-500"
              title="Open Tickets"
              value={support.open_tickets || 0}
              subtitle={`${support.active_sprints || 0} sprints at ${support.avg_sprint_completion || 0}% avg`}
              metricKey="open_tickets"
              goodUp={false}
            />
            <KpiCard
              icon={<Bug className="w-5 h-5 text-white" />}
              iconBg="bg-red-500"
              title="Production Bugs"
              value={support.production_bugs || 0}
              alert={(support.production_bugs || 0) > 5}
              subtitle={(support.production_bugs || 0) > 0 ? 'Customer impact risk' : 'All clear'}
              metricKey="production_bugs"
              goodUp={false}
            />
            {(support.cr_total || 0) > 0 && (
              <KpiCard
                icon={<Zap className="w-5 h-5 text-white" />}
                iconBg="bg-violet-500"
                title="Client Requirements"
                value={`${support.cr_pending || 0} pending`}
                subtitle={`${support.cr_completed || 0}/${support.cr_total || 0} completed`}
                alert={(support.cr_pending || 0) > 100}
              />
            )}
            {canSeeFinance && (
            <KpiCard
              icon={<Wallet className="w-5 h-5 text-white" />}
              iconBg="bg-rose-500"
              title="Outstanding Receivables"
              value={formatLakhs(receivables.outstanding_receivables || 0)}
              subtitle={(receivables.clients_with_pending || 0) > 0
                ? `${receivables.clients_with_pending} clients with pending`
                : 'All clients paid'}
              alert={(receivables.outstanding_receivables || 0) > 0}
              metricKey="outstanding_receivables"
              goodUp={false}
            />
            )}
            {canSeeFinance && (
            <KpiCard
              icon={<ShieldAlert className="w-5 h-5 text-white" />}
              iconBg="bg-amber-500"
              title="AMC Due This Month"
              value={formatLakhs(receivables.amc_due_this_month || 0)}
              subtitle={(receivables.amc_overdue_count || 0) > 0
                ? `${receivables.amc_overdue_count} overdue · ${receivables.amc_due_this_month_count || 0} due`
                : `${receivables.amc_due_this_month_count || 0} AMC renewals due`}
              alert={(receivables.amc_overdue_count || 0) > 0}
              metricKey="amc_due_this_month"
              goodUp={false}
            />
            )}
          </div>
        </div>
      </div>

      {/* ━━ Section 3: Cross-Department Signals ━━ */}
      {signals.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Critical / Red signals */}
          <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-foreground">Critical Signals</h3>
              <span className="text-xs text-muted-foreground ml-auto">{criticalSignals.length} issues</span>
            </div>
            {criticalSignals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-11 h-11 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center mb-2">
                  <AlertCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-sm text-foreground font-medium">No critical signals</p>
                <p className="text-xs text-muted-foreground">Everything is stable</p>
              </div>
            ) : (
              <div className="space-y-2">
                {criticalSignals.slice(0, 5).map((s, idx) => (
                  <div
                    key={idx}
                    className="relative flex items-start gap-3 p-3 rounded-xl border border-rose-200 dark:border-rose-500/30 dark:border-rose-500/20 bg-gradient-to-r from-rose-50/80 via-rose-50/40 to-transparent dark:from-rose-950/40 dark:via-rose-950/20 dark:to-transparent overflow-hidden"
                  >
                    <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-rose-400 to-rose-600 dark:from-rose-500 dark:to-rose-700 rounded-l-xl" />
                    <div className="shrink-0 w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-950/60 flex items-center justify-center ring-1 ring-rose-200 dark:ring-rose-500/30">
                      <Flame className="w-3.5 h-3.5 text-rose-500 dark:text-rose-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground leading-snug">{s.message}</p>
                      {s.departments && s.departments.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {s.departments.map(d => (
                            <span
                              key={d}
                              className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-rose-100/80 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30 dark:border-rose-500/20"
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Warnings */}
          <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-foreground">Warnings</h3>
              <span className="text-xs text-muted-foreground ml-auto">{warningSignals.length} items</span>
            </div>
            {warningSignals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-11 h-11 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center mb-2">
                  <AlertTriangle className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-sm text-foreground font-medium">No warnings</p>
                <p className="text-xs text-muted-foreground">All systems in range</p>
              </div>
            ) : (
              <div className="space-y-2">
                {warningSignals.slice(0, 5).map((s, idx) => (
                  <div
                    key={idx}
                    className="relative flex items-start gap-3 p-3 rounded-xl border border-amber-200 dark:border-amber-500/30 dark:border-amber-500/20 bg-gradient-to-r from-amber-50/80 via-amber-50/40 to-transparent dark:from-amber-950/40 dark:via-amber-950/20 dark:to-transparent overflow-hidden"
                  >
                    <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-amber-400 to-amber-600 dark:from-amber-500 dark:to-amber-700 rounded-l-xl" />
                    <div className="shrink-0 w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center ring-1 ring-amber-200 dark:ring-amber-500/30">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-sm font-medium text-foreground leading-snug">{s.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ━━ Section 4: Department Health + Alerts ━━ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Dept chart */}
        <div className="lg:col-span-2 rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-indigo-500" />
            <h3 className="font-semibold text-foreground">Department vs Target</h3>
          </div>
          {departmentHealthData.length === 0 ? (
            <Empty description="No department data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
              <div style={{ height: 240 }}>
                <Bar data={deptChartData} options={deptChartOptions} />
              </div>
              {/* Department cards below chart */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {departmentHealthData.map((dept, idx) => (
                  <div key={idx} className={`p-3 rounded-xl border ${
                    dept.status === 'critical' ? 'border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-950/30' :
                    dept.status === 'at_risk' ? 'border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-950/30' :
                    'border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-foreground">{dept.department}</span>
                      <div className={`w-2 h-2 rounded-full ${
                        dept.status === 'critical' ? 'bg-rose-500' :
                        dept.status === 'at_risk' ? 'bg-amber-500' : 'bg-emerald-500'
                      }`} />
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-tight">{dept.key_metric}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Alerts donut */}
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h3 className="font-semibold text-foreground">Alerts</h3>
            <Tag className="ml-auto">{alerts.length}</Tag>
          </div>
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[280px]">
              <div className="w-16 h-16 rounded-full bg-green-50 dark:bg-green-950/30 flex items-center justify-center mb-3">
                <Info className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-sm text-muted-foreground">All clear</p>
            </div>
          ) : (
            <div style={{ height: 240 }}>
              <Doughnut data={donutData} options={donutOptions} />
            </div>
          )}
        </div>
      </div>

      {/* Alert Detail Drawer */}
      <Drawer
        title="Alert Details"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
      >
        {selectedAlert && (
          <div className="space-y-4">
            <Tag color={selectedAlert.severity === 'critical' ? 'red' : selectedAlert.severity === 'warning' ? 'orange' : 'green'}>
              {selectedAlert.severity?.toUpperCase()}
            </Tag>
            <h3 className="text-lg font-semibold">{selectedAlert.title}</h3>
            <p className="text-muted-foreground">{selectedAlert.description || selectedAlert.message}</p>
            {selectedAlert.recommended_action && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200"><strong>Recommended:</strong> {selectedAlert.recommended_action}</p>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* KPI Drill-down Drawer */}
      <DataDrawer
        open={!!kpiDrawer}
        onClose={() => setKpiDrawer(null)}
        title={kpiDrawer?.title || 'Metric'}
        subtitle={kpiDrawer?.subtitle}
      >
        {kpiDrawer && (
          <>
            <div className="text-3xl font-bold text-foreground">{kpiDrawer.value}</div>
            <div className="rounded-xl border border-border p-4 bg-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Last 7 days
              </div>
              {trends.time_series?.length > 0 ? (
                <div className="space-y-1.5">
                  {trends.time_series.map((t) => (
                    <div key={t.date} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t.date}</span>
                      <span className="font-semibold text-foreground">{t[kpiDrawer.key] ?? 0}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No historical data yet — snapshots accumulate daily.</p>
              )}
            </div>
            {trends.deltas?.[kpiDrawer.key] && (
              <div className="rounded-xl border border-border p-4 bg-card space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Week-over-week
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current</span>
                  <span className="font-semibold">{trends.deltas[kpiDrawer.key].current}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Previous</span>
                  <span className="font-semibold">{trends.deltas[kpiDrawer.key].previous}</span>
                </div>
                {trends.deltas[kpiDrawer.key].delta_pct != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Change</span>
                    <span className="font-semibold">{trends.deltas[kpiDrawer.key].delta_pct.toFixed(1)}%</span>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => {
                setKpiDrawer(null);
                window.location.href = '/dashboard/trends';
              }}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
            >
              Open full Trends
            </button>
          </>
        )}
      </DataDrawer>
    </div>
  );
};

export default Dashboard;
